/**
 * Authentication service
 */
import apiClient, { setTokens, clearTokens, getRefreshToken } from './api.js';

/**
 * Login with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User data and tokens
 */
export async function login(email, password) {
  const response = await apiClient.post('/auth/login', {
    email,
    password,
  });

  const { token, refreshToken, user } = response.data;
  setTokens(token, refreshToken);

  return { user, token, refreshToken };
}

/**
 * Logout current user
 * @returns {Promise<void>}
 */
export async function logout() {
  const refresh = getRefreshToken();
  
  try {
    await apiClient.post('/auth/logout', {
      refreshToken: refresh,
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearTokens();
  }
}

/**
 * Refresh access token
 * @returns {Promise<Object>} New tokens
 */
export async function refreshAccessToken() {
  const refresh = getRefreshToken();
  
  if (!refresh) {
    throw new Error('No refresh token available');
  }

  const response = await apiClient.post('/auth/refresh', {
    refreshToken: refresh,
  });

  const { token, refreshToken: newRefreshToken } = response.data;
  setTokens(token, newRefreshToken);

  return { token, refreshToken: newRefreshToken };
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export function isAuthenticated() {
  return !!localStorage.getItem('accessToken');
}

/**
 * Get current user from localStorage
 * @returns {Object|null}
 */
export function getCurrentUser() {
  const userStr = localStorage.getItem('currentUser');
  return userStr ? JSON.parse(userStr) : null;
}

/**
 * Save current user to localStorage
 * @param {Object} user - User object
 */
export function saveCurrentUser(user) {
  localStorage.setItem('currentUser', JSON.stringify(user));
}

/**
 * Clear current user from localStorage
 */
export function clearCurrentUser() {
  localStorage.removeItem('currentUser');
}
